import { SwitchOptions } from "@/components/SwitchOptions";
import { capitalize, formatLocaleNumber } from "@/utils/formatUtils";
import { useWindowSize } from "@/utils/hooksUtils";
import { tInterval } from "@/utils/translationUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Card, Col, ConfigProvider, Descriptions, DescriptionsProps, Flex, Row, Select, Space } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import { useMemo, useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useGetDashboardCommissions } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { DashboardCommissionsPeriod, DashboardCommissionsReport } from "../../../enums";
import { DashboardCommissionsItem } from "../../../types";
import { DASHBOARD_LARGER_SCREEN_UPDATE_WIDTH, DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH } from "../../../utils";

enum DashboardCommissionType {
  CARRIER = "CARRIER",
  INDIVIDUAL = "INDIVIDUAL",
  GROUP = "GROUP",
}

enum DashboardCommissionsBatchType {
  PLUS = "PLUS",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
}

const isBigValue = (value?: number): boolean => numberOrZero(value) > 999999;

const getCommissionBatchTypeLabel = (entryName: string): string => {
  switch (entryName) {
    case "primaryValue":
      return t("dashboard.enums.commissionCompany.PRIMARY");
    case "secondaryValue":
      return t("dashboard.enums.commissionCompany.SECONDARY");
    default:
      return t("dashboard.enums.commissionCompany.PLUS");
  }
};

const getLabelFromDashboardCommissionsItem = (
  item: DashboardCommissionsItem,
  period: DashboardCommissionsPeriod,
): string => {
  const date = dayjs(item.startDate);

  switch (period) {
    case DashboardCommissionsPeriod.CURRENT_YEAR:
    case DashboardCommissionsPeriod.LAST_YEAR:
      return capitalize(date.format("MMM"));
    case DashboardCommissionsPeriod.AVERAGE:
      const endDate = dayjs(item.endDate);
      return tInterval("dashboard.commissions.helpers.months_interval", endDate.diff(date, "month") + 1);
    default:
      return date.year().toString();
  }
};

const getValueFromDashboardCommissionsItem = (
  item: DashboardCommissionsItem,
): Record<DashboardCommissionType, Record<DashboardCommissionsBatchType, number>> => {
  return {
    [DashboardCommissionType.INDIVIDUAL]: {
      [DashboardCommissionsBatchType.PRIMARY]: item.primaryIndividualValue,
      [DashboardCommissionsBatchType.SECONDARY]: item.secondaryIndividualValue,
      [DashboardCommissionsBatchType.PLUS]: item.primaryIndividualValue + item.secondaryIndividualValue,
    },
    [DashboardCommissionType.GROUP]: {
      [DashboardCommissionsBatchType.PRIMARY]: item.primaryGroupValue,
      [DashboardCommissionsBatchType.SECONDARY]: item.secondaryGroupValue,
      [DashboardCommissionsBatchType.PLUS]: item.primaryGroupValue + item.secondaryGroupValue,
    },
    [DashboardCommissionType.CARRIER]: {
      [DashboardCommissionsBatchType.PRIMARY]: item.primaryIndividualValue + item.primaryGroupValue,
      [DashboardCommissionsBatchType.SECONDARY]: item.secondaryIndividualValue + item.secondaryGroupValue,
      [DashboardCommissionsBatchType.PLUS]:
        item.primaryIndividualValue + item.primaryGroupValue + item.secondaryIndividualValue + item.secondaryGroupValue,
    },
  };
};

export const DashboardCommissionsWidget = () => {
  const { t } = useTranslation();

  const windowSize = useWindowSize();
  const hasHigherResolution = windowSize.innerWidth >= DASHBOARD_LARGER_SCREEN_UPDATE_WIDTH;

  const [commissionType, setCommissionType] = useState<DashboardCommissionType>(DashboardCommissionType.CARRIER);
  const [period, setPeriod] = useState<DashboardCommissionsPeriod>(DashboardCommissionsPeriod.TOTAL);
  const [batchType, setBatchType] = useState<DashboardCommissionsBatchType>(DashboardCommissionsBatchType.PLUS);
  const [reportUnit, setReportUnit] = useState<DashboardCommissionsReport>(DashboardCommissionsReport.POINTS);

  const { data } = useGetDashboardCommissions({ period: period, report: reportUnit });

  const result = useMemo(
    () =>
      data?.items.map((item) => {
        const label = getLabelFromDashboardCommissionsItem(item, data.period);
        const value = getValueFromDashboardCommissionsItem(item);

        return {
          name: label,
          primaryValue:
            batchType === DashboardCommissionsBatchType.PLUS || batchType === DashboardCommissionsBatchType.PRIMARY
              ? value[commissionType][DashboardCommissionsBatchType.PRIMARY]
              : undefined,
          secondaryValue:
            batchType === DashboardCommissionsBatchType.PLUS || batchType === DashboardCommissionsBatchType.SECONDARY
              ? value[commissionType][DashboardCommissionsBatchType.SECONDARY]
              : undefined,
          individualValue:
            commissionType === DashboardCommissionType.INDIVIDUAL || commissionType === DashboardCommissionType.CARRIER
              ? value[DashboardCommissionType.INDIVIDUAL][batchType]
              : undefined,
          groupValue:
            commissionType === DashboardCommissionType.GROUP || commissionType === DashboardCommissionType.CARRIER
              ? value[DashboardCommissionType.GROUP][batchType]
              : undefined,
        };
      }),
    [data, commissionType, batchType, reportUnit],
  );

  const individual = result?.reduce((sum, item) => sum + numberOrZero(item.individualValue), 0);
  const group = result?.reduce((sum, item) => sum + numberOrZero(item.groupValue), 0);
  const carrier = result?.reduce(
    (sum, item) => sum + numberOrZero(item.individualValue) + numberOrZero(item.groupValue),
    0,
  );

  const descriptionData: DescriptionsProps["items"] = [
    {
      key: DashboardCommissionType.INDIVIDUAL,
      label: <div>{t(`dashboard.enums.commissionPointsType.${DashboardCommissionType.INDIVIDUAL}`)}</div>,
      children: (
        <CountUp
          style={{ display: "block" }}
          end={numberOrZero(individual)}
          duration={4}
          separator={" "}
          suffix={reportUnit === DashboardCommissionsReport.POINTS ? "" : " €"}
        />
      ),
    },
    {
      key: DashboardCommissionType.GROUP,
      label: <div>{t(`dashboard.enums.commissionPointsType.${DashboardCommissionType.GROUP}`)}</div>,
      children: (
        <CountUp
          style={{ display: "block" }}
          end={numberOrZero(group)}
          duration={4}
          separator={" "}
          suffix={reportUnit === DashboardCommissionsReport.POINTS ? "" : " €"}
        />
      ),
    },
    {
      key: DashboardCommissionType.CARRIER,
      label: <div>{t(`dashboard.enums.commissionPointsType.${DashboardCommissionType.CARRIER}`)}</div>,
      children: (
        <CountUp
          style={{ display: "block" }}
          end={numberOrZero(carrier)}
          duration={4}
          separator={" "}
          suffix={reportUnit === DashboardCommissionsReport.POINTS ? "" : " €"}
        />
      ),
    },
  ];

  return (
    <Card
      title={
        windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? t("dashboard.commissions.title") : undefined
      }
      style={{ height: "100%", borderRadius: 18, display: "flex", flexDirection: "column", overflow: "hidden" }}
      styles={{
        header: { borderBottom: "none", fontSize: 28, paddingTop: 16 },
        body: { flex: 1, padding: "12px 24px" },
      }}
      extra={
        <ConfigProvider theme={{ token: { borderRadius: 18 } }}>
          <Space>
            <Select
              style={{ width: 130 }}
              options={Object.values(DashboardCommissionType).map((type) => ({
                label: t(`dashboard.enums.commissionPointsType.${type}`),
                value: type,
              }))}
              value={commissionType}
              onChange={setCommissionType}
            />

            <Select
              style={{ width: 130 }}
              options={Object.values(DashboardCommissionsPeriod).map((period) => ({
                label: t(`dashboard.enums.dashboardCommissionsPeriod.${period}`),
                value: period,
              }))}
              value={period}
              onChange={setPeriod}
            />

            <Select
              style={{ width: 160 }}
              options={Object.values(DashboardCommissionsBatchType).map((company) => ({
                label: t(`dashboard.enums.commissionCompany.${company}`),
                value: company,
              }))}
              value={batchType}
              onChange={setBatchType}
            />
          </Space>
        </ConfigProvider>
      }
    >
      <Flex vertical style={{ height: "100%" }}>
        {!hasHigherResolution ? (
          <Flex style={{ height: 32, marginBottom: 8 }}>
            <Descriptions
              style={{
                border: `1px solid ${cssVariables.colorHover}`,
                padding: "2px 12px 0",
                borderRadius: 18,
                textAlign: "center",
              }}
              styles={{ label: { alignItems: "center" }, content: { fontSize: 14 } }}
              items={descriptionData}
              column={3}
              size="small"
            />
          </Flex>
        ) : undefined}

        <Flex vertical flex={1} style={{ overflow: "hidden", position: "relative" }}>
          <Row gutter={rowGutter} style={{ height: "100%" }}>
            <Col span={hasHigherResolution ? 20 : 24} style={{ height: "100%" }}>
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 100, height: 220 }}>
                <BarChart margin={{ top: 25, bottom: 15 }} data={result} maxBarSize={50}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tickMargin={15} />
                  <YAxis axisLine={false} tickLine={false} />

                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const total = payload.reduce((sum, entry) => sum + numberOrZero(entry.value as number), 0);

                        return (
                          <div
                            style={{
                              background: "#fff",
                              border: "1px solid #ccc",
                              padding: "12px 16px 0 16px",
                              borderRadius: 18,
                            }}
                          >
                            <p>{label}</p>

                            {payload.map((entry) => (
                              <p key={String(entry.dataKey)} style={{ color: entry.color }}>
                                {getCommissionBatchTypeLabel(entry.name as string)}:{" "}
                                {formatLocaleNumber(numberOrZero(entry.value as number))}
                              </p>
                            ))}

                            <p>
                              <strong>
                                {t("dashboard.commissions.helpers.all")}: {formatLocaleNumber(numberOrZero(total))}
                              </strong>
                            </p>
                          </div>
                        );
                      }

                      return;
                    }}
                  />
                  <CartesianGrid vertical={false} strokeLinecap="butt" stroke="#f0f0f0" />

                  <Bar
                    dataKey="primaryValue"
                    fill={"#ffce2b"}
                    isAnimationActive
                    animationBegin={500}
                    animationDuration={1500}
                    animationEasing="ease"
                    stackId="a"
                    activeBar={{ stroke: "#323232" }}
                  />

                  <Bar
                    dataKey="secondaryValue"
                    fill={"#6d6e70"}
                    isAnimationActive
                    animationBegin={500}
                    animationDuration={1500}
                    animationEasing="ease"
                    stackId="a"
                    activeBar={{ stroke: "#323232" }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Col>

            {hasHigherResolution ? (
              <Col span={4}>
                <Descriptions
                  style={{
                    marginTop: 22,
                    border: `1px solid ${cssVariables.colorHover}`,
                    borderRadius: 18,
                    padding: "28px 2px",
                    textAlign: "center",
                  }}
                  styles={{
                    label: { textAlign: "center", display: "block", height: 21, width: "100%" },
                    content: {
                      justifyContent: "center",
                      fontSize: isBigValue(carrier) && hasHigherResolution ? 14 : 16,
                    },
                    root: { textAlign: "center" },
                  }}
                  items={descriptionData}
                  column={1}
                  colon={false}
                  layout="vertical"
                  size="small"
                />
              </Col>
            ) : undefined}
          </Row>
        </Flex>

        <Flex style={{ width: "100%" }} align="end">
          <div style={{ height: 24, width: 200 }} className="margin-bottom-tiny">
            <SwitchOptions
              size="small"
              options={[
                {
                  label: t("dashboard.commissions.enums.dashboardCommissionsReport.POINTS"),
                  value: DashboardCommissionsReport.POINTS,
                },
                {
                  label: t("dashboard.commissions.enums.dashboardCommissionsReport.AMOUNTS"),
                  value: DashboardCommissionsReport.AMOUNTS,
                },
              ]}
              onChange={setReportUnit}
              value={reportUnit}
            />
          </div>
        </Flex>
      </Flex>
    </Card>
  );
};
